import {
  PlanType,
  RadioButtonGroupMapping,
  RadioButtonType,
} from '@labrav/react-components'
import { camelize } from 'casing'
import { getDiscountedPriceValue, isNonEmptyNumber } from '@labrav/common'
import {
  AzurePlanDimension,
  Product,
  ProductPlan,
} from '../../../modules/productsListing/types'
import {
  AzureOfferPlan,
  AzurePriceInputOption,
  AzurePricingType,
  AzureRecurrentPriceField,
  OfferRecipients,
  OfferRecurrentPricingType,
  OfferStakeholders,
  RecurrentPriceModeType,
} from '../../../types/flyout'
import {
  buildAzureOfferStakeholdersForPayload,
  getContainerCustomMeters,
  getContainerPlanTcvValue,
  getContainerTcvFromMeters,
  getContainerTcvFromProductPlan,
  getInitialContainerPlanPrices,
  getMeterDisplayName,
  getMeterPricePerPaymentInUsd,
  getUpdatedContainerCustomMeters,
  hasCustomMeteringDimensions,
  isAzureContainerProduct,
  isAzureSystemMeter,
  isContainerDiscountPricingType,
  mapContainerAzurePlanFromApi,
  normalizeAzurePlanMeters,
  resolveContainerLabraPlanId,
} from '../ContainerPlan/containerPlanUtils'

export const VM_METER_PRICE_INPUT = {
  PER_CORE: 'perCore',
  FLAT: 'flat',
  PER_CORE_SIZE: 'perCoreSize',
  PER_MARKET_AND_CORE_SIZE: 'perMarketAndCoreSize',
} as const

export const vmPricingTypeMapping: RadioButtonGroupMapping[] = [
  {
    key: AzurePricingType.ABSOLUTE,
    label: 'Absolute',
    type: RadioButtonType.STANDARD,
  },
  {
    key: AzurePricingType.LABRA_PERCENTAGE,
    label: 'Discount',
    type: RadioButtonType.STANDARD,
  },
]

export const vmEmptyPlan = {
  displayName: '',
  planId: '',
  offerPricingType: 'saasNewCustomizedPlans',
  description: '',
  labraPlanId: '',
  discountPercentage: null,
  pricing: {
    recurrentPrice: {
      recurrentPriceMode: '',
      priceInputOption: 'usd',
      prices: [],
    },
  },
}

type VmMeterValue = Record<string, unknown>

type VmProduct = {
  productType?: PlanType | string
  plans?: ProductPlan[]
}

export const hasAzureVmCoreProductPlans = (product?: VmProduct) =>
  (product?.plans || []).some(planItem => {
    const productDimensions = planItem?.dimensions as
      | AzurePlanDimension
      | undefined
    const meters = normalizeAzurePlanMeters(productDimensions?.meters)

    return meters.some(meter => {
      const meterRecord = meter as unknown as VmMeterValue
      return (
        String(
          meterRecord.meterType || meterRecord.meter_type || ''
        ).toUpperCase() === 'VM_CORE'
      )
    })
  })

export const isAzureVmProduct = (product?: VmProduct) => {
  const productType = String(product?.productType ?? '').toUpperCase()

  if (
    productType === 'VM' ||
    productType === 'VIRTUAL_MACHINE' ||
    productType === 'VIRTUALMACHINE'
  ) {
    return true
  }

  return hasAzureVmCoreProductPlans(product)
}

export const isAzureContainerLikePrivateOfferProduct = (
  product?: VmProduct
) => {
  const isVmProduct = isAzureVmProduct(product)
  return isVmProduct || isAzureContainerProduct(product)
}

export const getAzurePrivateOfferProductFlags = (product?: VmProduct) => {
  const isVmProduct = isAzureVmProduct(product)
  const isContainerProduct = !isVmProduct && isAzureContainerProduct(product)

  return {
    isVmProduct,
    isContainerProduct,
    isContainerLikeProduct: isVmProduct || isContainerProduct,
  }
}

export const getVmMeterPriceInputOption = (meter?: VmMeterValue) => {
  if (!meter) {
    return ''
  }

  return String(
    meter.meterPriceInputOption || meter.meter_price_input_option || ''
  )
}

export const isVmSingleMeterPricingOption = (option?: string) =>
  option === VM_METER_PRICE_INPUT.PER_CORE ||
  option === VM_METER_PRICE_INPUT.FLAT

export const isVmMultiMeterPricingOption = (option?: string) =>
  option === VM_METER_PRICE_INPUT.PER_CORE_SIZE ||
  option === VM_METER_PRICE_INPUT.PER_MARKET_AND_CORE_SIZE

export const getVmPlanPriceInputOption = (productPlan?: ProductPlan) => {
  const productDimensions = productPlan?.dimensions as
    | AzurePlanDimension
    | undefined
  const meters = normalizeAzurePlanMeters(productDimensions?.meters)

  if (meters.length === 0) {
    return ''
  }

  return getVmMeterPriceInputOption(meters[0] as unknown as VmMeterValue)
}

export const shouldForceVmDiscountPricingType = (productPlan?: ProductPlan) =>
  isVmMultiMeterPricingOption(getVmPlanPriceInputOption(productPlan))

export const resolveVmMeterTypeForForm = (
  productPlan?: ProductPlan
): 'SYSTEM' | 'CUSTOM' =>
  shouldForceVmDiscountPricingType(productPlan) ? 'CUSTOM' : 'SYSTEM'

export const getVmPlanMeters = ({
  productPlan,
  pricesValue,
}: {
  productPlan: ProductPlan
  pricesValue?: AzureRecurrentPriceField[]
}) => {
  const productDimensions = productPlan?.dimensions as
    | AzurePlanDimension
    | undefined
  const meterType = resolveVmMeterTypeForForm(productPlan)
  const productsMeters = normalizeAzurePlanMeters(productDimensions?.meters)

  return productsMeters.reduce<Record<string, Record<string, unknown>>>(
    (acc, meter) => {
      const cloudDimensionId = meter.cloudDimensionId
      if (!cloudDimensionId) {
        return acc
      }

      const { includedQuantities, ...remainingMeterData } = meter

      acc[cloudDimensionId] = {
        ...remainingMeterData,
        meterType,
        meterPriceInputOption: getVmMeterPriceInputOption(
          meter as unknown as VmMeterValue
        ),
        meteringDimensionName: getMeterDisplayName(
          remainingMeterData,
          cloudDimensionId
        ),
        includedQuantities: pricesValue?.length
          ? includedQuantities?.filter(
              item =>
                item?.billingTerm?.type === pricesValue[0]?.billingTerm?.type &&
                item?.billingTerm?.value === pricesValue[0]?.billingTerm?.value
            )
          : meterType === 'CUSTOM'
            ? []
            : includedQuantities,
      }

      return acc
    },
    {}
  )
}

export const getInitialVmPlanPrices = (
  productPlan: ProductPlan
): AzureRecurrentPriceField[] => {
  if (!isVmSingleMeterPricingOption(getVmPlanPriceInputOption(productPlan))) {
    return []
  }

  return getInitialContainerPlanPrices(productPlan)
}

export const convertVmProductPlanPricingToOffer = (
  productPlan: ProductPlan
) => {
  const pricesValue = getInitialVmPlanPrices(productPlan)
  const meters = getVmPlanMeters({ productPlan, pricesValue })

  const pricingData = {
    recurrentPrice: {
      recurrentPriceMode: RecurrentPriceModeType.FLATRATE,
      priceInputOption: AzurePriceInputOption.USD,
      userLimits: {
        min: productPlan?.minQuantity,
        max: productPlan?.maxQuantity,
        minEnable: true,
        maxEnable: true,
      },
      prices: pricesValue,
    },
    customMeters: {
      meters,
    },
  }

  return {
    pricingData,
    originalPricingData: {
      ...pricingData,
      customMeters: {
        meters: getVmPlanMeters({ productPlan }),
      },
      recurrentPrice: {
        ...pricingData.recurrentPrice,
        prices: getInitialVmPlanPrices(productPlan),
      },
    },
  }
}

export const convertVmProductPlanToOfferPlan = (productPlan?: ProductPlan) => {
  if (!productPlan) {
    return productPlan
  }

  const { pricingData, originalPricingData } =
    convertVmProductPlanPricingToOffer(productPlan)

  return {
    planId: productPlan.planId,
    labraPlanId: productPlan.planId,
    azurePlanId: productPlan.azurePlanId,
    description: productPlan.description,
    displayName: productPlan.displayName,
    offerPricingType: OfferRecurrentPricingType.EDITEXISTINGOFFERPRICINGONLY,
    pricing: pricingData,
    originalPricing: originalPricingData,
    discountPercentage: undefined,
  }
}

export const hasVmTcvPricing = (plan?: AzureOfferPlan) => {
  const planRecord = plan as Record<string, unknown> | undefined
  const absolutePrice = planRecord?.absolutePrice ?? planRecord?.absolute_price

  if (isNonEmptyNumber(absolutePrice as number | undefined)) {
    return true
  }

  if (plan?.pricing?.recurrentPrice?.prices?.[0]) {
    return true
  }

  const pricingRecord = plan?.pricing as Record<string, unknown> | undefined
  if (pricingRecord?.systemMeterPricing) {
    return true
  }

  const meters = plan?.pricing?.customMeters?.meters
  if (!meters) {
    return false
  }

  return Object.values(meters).some(meter => isAzureSystemMeter(meter))
}

export const hasVmMultiMeterDimensions = (plan?: AzureOfferPlan) =>
  hasCustomMeteringDimensions(plan)

export const shouldShowVmTcv = (
  plan?: AzureOfferPlan,
  product?: { productType?: PlanType | string }
) =>
  isAzureVmProduct(product) &&
  hasVmTcvPricing(plan) &&
  !hasVmMultiMeterDimensions(plan)

export const getVmSystemMeterPriceTitle = (
  _plan?: AzureOfferPlan,
  azurePricingType?: AzurePricingType | string,
  _product?: { plans?: ProductPlan[] }
) =>
  azurePricingType === AzurePricingType.LABRA_PERCENTAGE
    ? 'Discount price per hour'
    : 'Price per hour'

export const getVmPlanTcvValue = (plan?: AzureOfferPlan) => {
  const planRecord = plan as Record<string, unknown> | undefined
  const absolutePrice = planRecord?.absolutePrice ?? planRecord?.absolute_price

  if (isNonEmptyNumber(absolutePrice as number | undefined)) {
    return absolutePrice as number
  }

  return getContainerPlanTcvValue(plan)
}

export const getVmTcvFromMeters = getContainerTcvFromMeters

export const getVmCustomMeters = getContainerCustomMeters

export const resetVmPlansForPricingType = ({
  plans,
  product,
}: {
  plans: AzureOfferPlan[]
  product?: Product
}) =>
  plans.map(planItem => {
    if (!planItem?.labraPlanId) {
      return planItem
    }

    const productPlan = product?.plans?.find(
      item => item?.planId === planItem.labraPlanId
    )

    if (!productPlan) {
      return {
        ...planItem,
        discountPercentage: undefined,
      }
    }

    return convertVmProductPlanToOfferPlan(productPlan) as AzureOfferPlan
  })

const getVmOriginalTcvForDiscount = (
  plan: AzureOfferPlan,
  product?: Product
) => {
  const originalPricingPlan = plan?.originalPricing
    ? ({ pricing: plan.originalPricing } as AzureOfferPlan)
    : undefined

  const productPlan = product?.plans?.find(
    item => item.planId === plan.labraPlanId
  )

  return (
    getVmPlanTcvValue(originalPricingPlan) ??
    getVmTcvFromMeters(plan?.originalPricing?.customMeters?.meters) ??
    (productPlan ? getContainerTcvFromProductPlan(productPlan) : undefined)
  )
}

const normalizeVmPriceForComparison = (value: number) =>
  Number.parseFloat(value.toFixed(5))

export const getVmPlanPublicTcv = (
  plan?: AzureOfferPlan,
  product?: Product
) => {
  if (!plan?.labraPlanId) {
    return undefined
  }

  return getVmOriginalTcvForDiscount(plan, product)
}

export const isVmAbsolutePriceSameAsPublic = ({
  plan,
  product,
  absolutePrice,
}: {
  plan?: AzureOfferPlan
  product?: Product
  absolutePrice?: number | null
}) => {
  const publicPrice = getVmPlanPublicTcv(plan, product)

  if (
    !isNonEmptyNumber(publicPrice) ||
    publicPrice! <= 0 ||
    !isNonEmptyNumber(absolutePrice ?? undefined)
  ) {
    return false
  }

  return (
    normalizeVmPriceForComparison(absolutePrice!) ===
    normalizeVmPriceForComparison(publicPrice!)
  )
}

export const updateVmPlanDiscount = ({
  plan,
  product,
  discountPercentage,
}: {
  plan: AzureOfferPlan
  product: Product
  discountPercentage?: number
}) => {
  if (hasVmMultiMeterDimensions(plan)) {
    return {
      ...plan,
      discountPercentage,
    }
  }

  if (!hasVmTcvPricing(plan)) {
    return {
      ...plan,
      discountPercentage,
    }
  }

  const originalPrice = getVmOriginalTcvForDiscount(plan, product) ?? 0

  if (!isNonEmptyNumber(originalPrice) || originalPrice <= 0) {
    return {
      ...plan,
      discountPercentage,
    }
  }

  const discountedTcv = getDiscountedPriceValue({
    price: originalPrice,
    discount: discountPercentage || 0,
  })

  return {
    ...plan,
    discountPercentage,
    pricing: {
      ...plan.pricing,
      recurrentPrice: {
        ...(plan.pricing?.recurrentPrice || {}),
        prices: [
          {
            ...(plan.pricing?.recurrentPrice?.prices?.[0] || {}),
            pricePerPaymentInUsd: discountedTcv,
          },
        ],
      },
    },
  }
}

export const buildVmPlanPayloadItem = ({
  plan,
  azurePricingType,
}: {
  plan: AzureOfferPlan
  azurePricingType?: AzurePricingType | string
}) => {
  const { labraPlanId, discountPercentage } = plan

  if (isContainerDiscountPricingType(azurePricingType)) {
    return {
      labraPlanId,
      discountType: 'percentage',
      discountPercentage,
    }
  }

  return {
    labraPlanId,
    discountType: 'absolute',
    absolutePrice: getVmPlanTcvValue(plan),
  }
}

const filterVmOfferRecipients = (offerRecipients: OfferRecipients[] = []) =>
  offerRecipients.filter(
    offerRecipientItem =>
      String(offerRecipientItem.firstName || '').trim() !== '' ||
      String(offerRecipientItem.lastName || '').trim() !== '' ||
      String(offerRecipientItem.email || '').trim() !== '' ||
      String(offerRecipientItem.title || '').trim() !== ''
  )

export const buildAzureVmPrivateOfferPayload = ({
  privateOfferName,
  crmOrderObjectId,
  crmOpportunityId,
  cloudProductId,
  billingAccountId,
  variableStartDate,
  existingContractRenewal,
  offerExpirationDate,
  acceptBy,
  preparedBy,
  eulaType,
  offerDescription,
  companyName,
  emailCustomMessage,
  offerRecipients,
  offerStakeholders,
  customOfferStakeholders,
  plans,
  azurePricingType,
  offerStartDate,
}: {
  privateOfferName: string
  crmOrderObjectId?: string
  crmOpportunityId?: string
  cloudProductId?: string
  billingAccountId?: string
  variableStartDate?: boolean
  existingContractRenewal?: boolean
  offerExpirationDate?: string | null
  acceptBy?: string | null
  preparedBy?: string
  eulaType?: string
  offerDescription?: string | null
  companyName?: string | null
  emailCustomMessage?: string | null
  offerRecipients?: OfferRecipients[]
  offerStakeholders?: OfferStakeholders[]
  customOfferStakeholders?: OfferStakeholders[]
  plans?: AzureOfferPlan[]
  azurePricingType?: AzurePricingType | string
  offerStartDate?: string | null
}) => ({
  name: privateOfferName,
  crmOrderObjectId: crmOrderObjectId || crmOpportunityId,
  cloudProductId,
  billingAccountId,
  privateOfferType: 'customerPromotion',
  ...(variableStartDate !== undefined ? { variableStartDate } : {}),
  existingContractRenewal: existingContractRenewal ?? false,
  ...(offerExpirationDate ? { offerExpirationDate } : {}),
  ...(acceptBy ? { acceptBy } : {}),
  ...(!variableStartDate && offerStartDate ? { offerStartDate } : {}),
  ...(preparedBy ? { preparedBy } : {}),
  ...(eulaType ? { eulaType } : {}),
  ...(offerDescription ? { offerDescription } : {}),
  ...(companyName ? { companyName } : {}),
  ...(emailCustomMessage ? { emailCustomMessage } : {}),
  offerRecipients: filterVmOfferRecipients(offerRecipients),
  offerStakeholders: buildAzureOfferStakeholdersForPayload({
    crmOpportunityId,
    offerStakeholders,
    customOfferStakeholders,
  }),
  plans: (plans || [])
    .filter(planItem => Boolean(planItem?.labraPlanId))
    .map(planItem =>
      buildVmPlanPayloadItem({
        plan: planItem,
        azurePricingType,
      })
    ),
})

const isVmSimplifiedApiPlan = (apiPlan: Record<string, unknown>) => {
  const camelizedPlan = camelize(apiPlan) as Record<string, unknown>
  const pricing = camelizedPlan.pricing

  if (
    pricing &&
    typeof pricing === 'object' &&
    Object.keys(pricing).length > 0
  ) {
    return false
  }

  const discountType = String(
    camelizedPlan.discountType || camelizedPlan.discount_type || ''
  ).toLowerCase()

  return (
    discountType === 'percentage' ||
    discountType === 'absolute' ||
    camelizedPlan.absolutePrice !== undefined ||
    camelizedPlan.absolute_price !== undefined
  )
}

export const mapVmAzurePlanFromApi = ({
  apiPlan,
  productPlan,
  product,
}: {
  apiPlan: Record<string, unknown>
  productPlan?: ProductPlan
  product?: Product
}): AzureOfferPlan => {
  if (!isVmSimplifiedApiPlan(apiPlan)) {
    return mapContainerAzurePlanFromApi({ apiPlan, productPlan, product })
  }

  const camelizedPlan = camelize(apiPlan) as Record<string, unknown>
  const labraPlanId =
    (camelizedPlan.labraPlanId as string) ||
    (camelizedPlan.labra_plan_id as string)
  const discountType = String(
    camelizedPlan.discountType || camelizedPlan.discount_type || ''
  ).toLowerCase()
  const discountPercentage =
    (camelizedPlan.discountPercentage as number | null | undefined) ??
    (camelizedPlan.discount_percentage as number | null | undefined)
  const absolutePrice =
    (camelizedPlan.absolutePrice as number | null | undefined) ??
    (camelizedPlan.absolute_price as number | null | undefined)
  const basePlan = productPlan
    ? convertVmProductPlanToOfferPlan(productPlan)
    : undefined

  if (
    discountType === 'absolute' &&
    isNonEmptyNumber(absolutePrice ?? undefined)
  ) {
    if (basePlan) {
      return {
        ...basePlan,
        labraPlanId: labraPlanId || basePlan.labraPlanId,
        discountPercentage: undefined,
        pricing: {
          ...basePlan.pricing,
          recurrentPrice: {
            ...(basePlan.pricing?.recurrentPrice || {}),
            prices: [{ pricePerPaymentInUsd: absolutePrice as number }],
          },
        },
      } as AzureOfferPlan
    }

    return {
      labraPlanId,
      discountPercentage: undefined,
      pricing: {
        recurrentPrice: {
          prices: [{ pricePerPaymentInUsd: absolutePrice as number }],
        },
      },
    } as AzureOfferPlan
  }

  if (
    discountType === 'percentage' &&
    isNonEmptyNumber(discountPercentage ?? undefined)
  ) {
    if (basePlan && product) {
      const discountedPlan = updateVmPlanDiscount({
        plan: {
          ...basePlan,
          labraPlanId: labraPlanId || basePlan.labraPlanId,
        } as AzureOfferPlan,
        product,
        discountPercentage: discountPercentage ?? undefined,
      })

      return (discountedPlan || {
        ...basePlan,
        labraPlanId: labraPlanId || basePlan.labraPlanId,
        discountPercentage,
      }) as AzureOfferPlan
    }

    return {
      ...(basePlan || {}),
      labraPlanId: labraPlanId || basePlan?.labraPlanId,
      discountPercentage,
    } as AzureOfferPlan
  }

  return {
    ...(basePlan || {}),
    ...camelizedPlan,
    labraPlanId: labraPlanId || basePlan?.labraPlanId,
  } as AzureOfferPlan
}

export const mapVmAzurePlansFromApi = ({
  plans,
  product,
}: {
  plans?: Array<Record<string, unknown>>
  product?: Product
}) =>
  (plans || []).map(plan => {
    const camelizedPlan = camelize(plan) as Record<string, unknown>
    const labraPlanId =
      (camelizedPlan.labraPlanId as string) ||
      (camelizedPlan.labra_plan_id as string)
    const resolvedLabraPlanId = resolveContainerLabraPlanId({
      apiLabraPlanId: labraPlanId,
      product,
    })
    const productPlan = product?.plans?.find(
      item =>
        item.planId === resolvedLabraPlanId || item.displayName === labraPlanId
    )

    const mappedPlan = mapVmAzurePlanFromApi({
      apiPlan: plan,
      productPlan,
      product,
    })

    if (!productPlan) {
      return mappedPlan
    }

    const meterType = resolveVmMeterTypeForForm(productPlan)
    const meters = mappedPlan.pricing?.customMeters?.meters

    if (!meters) {
      return mappedPlan
    }

    const updatedMeters = Object.fromEntries(
      Object.entries(meters).map(([key, meter]) => [
        key,
        {
          ...meter,
          meterType,
        },
      ])
    )

    return {
      ...mappedPlan,
      pricing: {
        ...mappedPlan.pricing,
        customMeters: {
          ...mappedPlan.pricing?.customMeters,
          meters: updatedMeters,
        },
      },
    }
  })

export const shouldDisableVmAbsolutePricing = ({
  plans,
  product,
}: {
  plans?: AzureOfferPlan[]
  product?: Product
}) => {
  if (!isAzureVmProduct(product)) {
    return false
  }

  return (plans || []).some(plan => {
    if (!plan?.labraPlanId) {
      return false
    }

    const productPlan = product?.plans?.find(
      item => item.planId === plan.labraPlanId
    )

    return shouldForceVmDiscountPricingType(productPlan)
  })
}

export const resolveVmPricingTypeForPlanSelection = ({
  productPlan,
  currentPricingType,
}: {
  productPlan?: ProductPlan
  currentPricingType?: AzurePricingType | string
}) =>
  shouldForceVmDiscountPricingType(productPlan)
    ? AzurePricingType.LABRA_PERCENTAGE
    : currentPricingType

export {
  getUpdatedContainerCustomMeters,
  getContainerTcvFromProductPlan,
  getMeterPricePerPaymentInUsd,
  isNonEmptyNumber,
}
